import React, { useCallback, useEffect, useRef } from 'react';
import { FiCheck, FiMinus, FiPlus } from 'react-icons/fi';
import { FaCalculator, FaWeightScale } from 'react-icons/fa6';
import { usesLiveScaleForPosFav } from '../utils/posFavInteraction';

export const POS_FAV_LONG_PRESS_MS = 550;
const POINTER_MOVE_TOLERANCE_PX = 12;

const formatScaleBubble = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return `${numeric.toLocaleString('de-DE', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })} kg`;
};

const usePressGesture = ({ disabled, onTap, onHold }) => {
  const holdTimerRef = useRef(null);
  const suppressTimerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const startPointRef = useRef(null);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    startPointRef.current = null;
  }, []);

  const clearSuppressTimer = useCallback(() => {
    if (suppressTimerRef.current) {
      window.clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearHoldTimer();
    clearSuppressTimer();
  }, [clearHoldTimer, clearSuppressTimer]);

  const handlePointerDown = useCallback((event) => {
    if (disabled || event.button !== 0) return;
    clearHoldTimer();
    clearSuppressTimer();
    suppressClickRef.current = false;
    startPointRef.current = {
      x: Number(event.clientX) || 0,
      y: Number(event.clientY) || 0,
    };
    holdTimerRef.current = window.setTimeout(() => {
      holdTimerRef.current = null;
      startPointRef.current = null;
      suppressClickRef.current = true;
      onHold?.();
      suppressTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = false;
        suppressTimerRef.current = null;
      }, 900);
    }, POS_FAV_LONG_PRESS_MS);
  }, [clearHoldTimer, clearSuppressTimer, disabled, onHold]);

  const handlePointerMove = useCallback((event) => {
    if (!startPointRef.current || !holdTimerRef.current) return;
    const deltaX = Math.abs((Number(event.clientX) || 0) - startPointRef.current.x);
    const deltaY = Math.abs((Number(event.clientY) || 0) - startPointRef.current.y);
    if (deltaX > POINTER_MOVE_TOLERANCE_PX || deltaY > POINTER_MOVE_TOLERANCE_PX) {
      clearHoldTimer();
    }
  }, [clearHoldTimer]);

  const handleClick = useCallback((event) => {
    if (disabled) return;
    if (suppressClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
      clearSuppressTimer();
      return;
    }
    onTap?.();
  }, [clearSuppressTimer, disabled, onTap]);

  return {
    onClick: handleClick,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: clearHoldTimer,
    onPointerCancel: clearHoldTimer,
    onPointerLeave: clearHoldTimer,
    onContextMenu: (event) => {
      if (!disabled) event.preventDefault();
    },
  };
};

const PosFavPriceCard = ({
  slot,
  onIncrement,
  onDecrement,
  onKeypad,
  scaleWeightKg = null,
  scaleConnected = false,
  interactionDisabled = false,
}) => {
  const disabled = Boolean(slot?.disabled || interactionDisabled);
  const usesLiveScale = usesLiveScaleForPosFav({
    usesScale: slot?.useScaleWeight,
    scaleConnected,
  });
  const scaleBubble = usesLiveScale ? formatScaleBubble(scaleWeightKg) : null;
  const handleKeypad = useCallback(() => {
    if (!disabled) onKeypad?.(slot);
  }, [disabled, onKeypad, slot]);
  const decrementGesture = usePressGesture({
    disabled,
    onTap: () => onDecrement?.(slot),
    onHold: handleKeypad,
  });
  const incrementGesture = usePressGesture({
    disabled,
    onTap: () => onIncrement?.(slot),
    onHold: handleKeypad,
  });
  const cartLabel = usesLiveScale
    ? `${slot?.scaleReadingCount || 0} ${slot?.scaleReadingCount === 1 ? 'pesaje' : 'pesajes'} · ${slot?.quantityLabel || '0'} kg`
    : `${slot?.quantityLabel || '0'} en carrito`;

  return (
    <article
      className={`group relative min-h-[168px] select-none touch-manipulation overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
        disabled
          ? 'border-gray-200 opacity-75 grayscale'
          : 'border-gray-200 hover:border-blue-200 hover:shadow-lg'
      }`}
      title={slot?.invalidReason
        || (interactionDisabled ? 'No tienes permisos para editar esta venta.' : '')
        || `${slot?.productName || ''} ${slot?.varietyName || ''}`.trim()}
      aria-disabled={disabled}
    >
      <div className="absolute inset-0 z-10 grid grid-cols-2">
        <button
          type="button"
          {...decrementGesture}
          disabled={disabled}
          className={`border-r border-dashed border-gray-200 transition focus:z-20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-400 ${
            disabled
              ? 'cursor-not-allowed bg-gray-50/50'
              : 'bg-transparent hover:bg-red-50/80 active:bg-red-100'
          }`}
          aria-label={`Quitar ${slot?.varietyName || 'precio favorito'}. Mantén presionado para cantidad exacta.`}
        />
        <button
          type="button"
          {...incrementGesture}
          disabled={disabled}
          className={`transition focus:z-20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-400 ${
            disabled
              ? 'cursor-not-allowed bg-gray-50/50'
              : 'bg-transparent hover:bg-emerald-50/80 active:bg-emerald-100'
          }`}
          aria-label={`${usesLiveScale ? 'Confirmar lectura de balanza para' : 'Agregar uno de'} ${slot?.varietyName || 'precio favorito'}. Mantén presionado para cantidad exacta.`}
        />
      </div>

      <div className="pointer-events-none relative z-20 flex min-h-[168px] flex-col p-2.5">
        <div className="flex min-w-0 items-start gap-2">
          <div className="relative shrink-0">
            <img
              src={slot?.imageSrc || 'https://placehold.co/200'}
              alt=""
              className="h-11 w-11 rounded-lg border border-white object-cover shadow"
            />
            {!usesLiveScale && Number(slot?.currentQuantity) > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1 text-[9px] font-black text-white shadow">
                {slot.quantityLabel}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[9px] font-bold uppercase tracking-[0.1em] text-gray-400">
              {slot?.productName || 'Producto'}
            </div>
            <h3 className="mt-0.5 line-clamp-2 text-xs font-bold leading-tight text-gray-900">
              {slot?.varietyName || 'Variedad'}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700">
                {slot?.metricUnit || 'Precio'}
              </span>
              {slot?.useScaleWeight && (
                <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                  usesLiveScale
                    ? scaleBubble
                      ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200'
                      : 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'
                    : 'bg-sky-100 text-sky-800 ring-1 ring-sky-200'
                }`}>
                  <FaWeightScale aria-hidden="true" />
                  {usesLiveScale ? scaleBubble || 'Sin lectura' : 'Manual'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-end justify-between gap-1.5">
          <div className={`text-lg font-black tracking-tight ${disabled ? 'text-gray-400' : 'text-blue-700'}`}>
            {slot?.priceLabel || '--'}
          </div>
          <div className="max-w-[56%] rounded-full bg-gray-100 px-1.5 py-1 text-right text-[9px] font-semibold leading-none text-gray-600">
            {cartLabel}
          </div>
        </div>

        {slot?.description && (
          <p className="mt-1 line-clamp-1 text-[10px] leading-snug text-gray-500">
            {slot.description}
          </p>
        )}
        {slot?.invalidReason && (
          <p className="mt-1 line-clamp-2 text-[10px] font-semibold leading-snug text-amber-700">{slot.invalidReason}</p>
        )}

        <div className="mt-auto grid grid-cols-[1fr_auto_1fr] items-end gap-1 pt-2 text-[9px] font-bold uppercase tracking-wide">
          <div className="flex items-center gap-1 text-left text-red-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
              <FiMinus size={12} />
            </span>
            {usesLiveScale ? <span className="sr-only">Quitar</span> : <span>-1</span>}
          </div>
          <div className="flex flex-col items-center text-[8px] normal-case leading-none tracking-normal text-gray-400">
            <FaCalculator size={11} />
            <span>Mantén</span>
          </div>
          <div className="flex items-center justify-end gap-1 text-right text-emerald-700">
            {usesLiveScale ? <span className="sr-only">Confirmar</span> : <span>+1</span>}
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100">
              {usesLiveScale ? <FiCheck size={12} /> : <FiPlus size={12} />}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default PosFavPriceCard;
